import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
// api
import { fetchPigPulseToken } from 'endpoints/pigPulse';
// utils
import classnames from 'classnames/bind';
import mixpanel from 'mixpanel-browser';
// styles
import styles from './PigPulsePanel.module.scss';

const cn = classnames.bind(styles);

const PigPulsePanel = ({ closePortal, currentUser }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [uiUrl, setUiUrl] = useState('');
  const iframeRef = useRef(null);
  const authRef = useRef({ token: '', origin: '' });

  const postToken = () => {
    const iframe = iframeRef.current;
    const { token, origin } = authRef.current;
    if (iframe && iframe.contentWindow && token) {
      iframe.contentWindow.postMessage({ type: 'pigpulse:token', token }, origin);
    }
  };

  // Initial load shows the preloader; token refreshes keep the iframe mounted
  const loadToken = () => {
    setHasError(false);
    fetchPigPulseToken()
      .then(({ token, ui_url }) => {
        authRef.current = { token, origin: new URL(ui_url).origin };
        setUiUrl(ui_url);
        setIsLoading(false);
        postToken();
      })
      .catch(() => {
        setIsLoading(false);
        setHasError(true);
      });
  };

  useEffect(() => {
    loadToken();
    mixpanel.track('Feature Used', {
      user_id: currentUser.id,
      feature_name: 'PigPulsePanel',
      action: 'Open Panel',
    });

    const onMessage = (event) => {
      if (!authRef.current.origin || event.origin !== authRef.current.origin) return;
      const type = event.data && event.data.type;
      if (type === 'pigpulse:ready') postToken();
      if (type === 'pigpulse:token-expired') loadToken();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className={cn('pig-pulse-panel')}>
      <div className={cn('panel-header')}>
        <span className={cn('title')}>PigPulse</span>
        <i className={cn('fa fa-times', 'close-icon')} onClick={closePortal} />
      </div>
      <div className={cn('panel-body')}>
        {isLoading && <Preloader isActive isCentered />}
        {hasError && (
          <div className={cn(uiUrl ? 'error-banner' : 'error-state')}>
            <FormattedMessage id="component.pigPulsePanel.error" />
            <button className={cn('retry-button')} onClick={loadToken}>
              <FormattedMessage id="general.tryAgain" />
            </button>
          </div>
        )}
        {uiUrl && (
          <iframe
            ref={iframeRef}
            src={`${uiUrl}?embed=everypig`}
            title="PigPulse"
            className={cn('frame')}
            onLoad={postToken}
          />
        )}
      </div>
    </div>
  );
};

PigPulsePanel.propTypes = {
  closePortal: T.func,
  currentUser: T.object.isRequired,
};

export default connect(
  (state) => ({
    currentUser: state.auth.user,
  })
)(PigPulsePanel);
